import { type ReactNode } from 'react';
import { ExternalLink, Info, X } from 'lucide-react';

import { Button } from '../Button/Button';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogTitle,
} from '../Dialog/Dialog';
import { cn } from '@/util/utils';

/*
  The prototype's shared preview window, doc-preview (scripts/components/doc-preview.js): a
  document or a picture shown large, with a header naming it, the thing itself on a muted ground,
  and a footer carrying one line of note and the actions that apply to it. The prototype opens
  DHL labels, invoices, credit notes, lookbook pages and outfit covers in it; the first caller
  here is an outfit's cover (ALS-235), and the others arrive with their screens.

  The close X sits at the right edge of the header, as ALS-235 asks and every
  preview in the prototype draws it (c30e113). Escape, the X and the backdrop all close it, and
  because it is its own layer, opened over a side sheet it closes alone and leaves the sheet
  where it was.
*/

export interface DocumentPreviewProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    subtitle?: string;
    /** The footer's one line, with an info glyph before it. */
    note?: string;
    /** The footer's buttons, on the right. */
    actions?: ReactNode;
    /**
     * Offers Print, after the actions: the prototype's sheets that are drawn rather than opened
     * from a file (a lookbook page, an invoice). It prints the sheet alone, nothing around it.
     */
    printable?: boolean;
    printLabel?: string;
    /**
     * `centre` (the default) centres one sheet on the muted ground; `stack` lays several one under
     * the other, scrolled from the top, as a book's pages are read (ALS-84), and prints one a sheet.
     */
    layout?: 'centre' | 'stack';
    /** What is being previewed. Centred on the muted ground and scrolled when it is taller. */
    children: ReactNode;
    className?: string;
}

export function DocumentPreview({
    open,
    onOpenChange,
    title,
    subtitle,
    note,
    actions,
    printable = false,
    printLabel = 'Print',
    layout = 'centre',
    children,
    className,
}: DocumentPreviewProps) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                showCloseButton={false}
                data-slot="document-preview"
                className={cn(
                    'flex max-h-[calc(100dvh-32px)] w-[min(860px,calc(100vw-32px))] flex-col gap-0 overflow-hidden p-0',
                    className,
                )}
            >
                <div className="border-border flex items-center gap-4 border-b px-5 py-4">
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <DialogTitle className="truncate">{title}</DialogTitle>
                        {subtitle ? (
                            <DialogDescription className="truncate">{subtitle}</DialogDescription>
                        ) : null}
                    </div>
                    <DialogClose asChild>
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label="Close preview"
                            className="shrink-0 pointer-coarse:size-11"
                        >
                            <X />
                        </Button>
                    </DialogClose>
                </div>
                <div
                    data-slot="document-preview-stage"
                    data-layout={layout}
                    className={cn(
                        'bg-muted flex min-h-0 flex-1 overflow-auto p-4 sm:p-6',
                        layout === 'stack'
                            ? 'flex-col items-center justify-start gap-4'
                            : 'items-center justify-center',
                    )}
                >
                    {children}
                </div>
                {note || actions || printable ? (
                    <div className="border-border flex flex-wrap items-center gap-3 border-t px-5 py-3">
                        {note ? (
                            // A width of its own, so on a phone the buttons go to a second line
                            // rather than squeezing the note to a word a line (ALS-96, ALS-378).
                            <p className="text-muted-foreground flex min-w-0 flex-[1_1_14rem] items-center gap-2 text-sm">
                                <Info className="size-4 shrink-0" aria-hidden />
                                <span>{note}</span>
                            </p>
                        ) : null}
                        {actions || printable ? (
                            <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                                {actions}
                                {printable ? (
                                    <Button size="sm" onClick={printSheet}>
                                        <ExternalLink />
                                        {printLabel}
                                    </Button>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </DialogContent>
        </Dialog>
    );
}

/**
 * The browser's print, of the sheet alone: globals.css hides everything but the preview's stage
 * while `is-printing-doc` is on the page, and the class goes once the print dialog closes.
 */
function printSheet() {
    document.body.classList.add('is-printing-doc');
    window.addEventListener('afterprint', () => document.body.classList.remove('is-printing-doc'), {
        once: true,
    });
    window.print();
}
